const lesson = (lessonId: number | null | undefined) =>
  ['lessons', lessonId] as const;

const exam = (examId: number | undefined) => ['exams', examId] as const;

export const queryKeys = {
  user: () => ['user'] as const,
  userAvatars: () => ['user', 'avatars'] as const,
  userCards: () => ['user', 'cards'] as const,
  users: () => ['users'] as const,
  usersAvailability: (pseudo?: string) =>
    ['users', 'availability', pseudo] as const,
  userProfiles: () => ['user_profile'] as const,
  userProfile: (userId: string | number) => ['user_profile', userId] as const,
  refereesCount: () => ['referees_count'] as const,
  appUsageStats: (userId: number) => ['appUsageStats', userId] as const,
  childProgress: (token: string | null | undefined) =>
    ['child-progress', token] as const,
  funnelFoxFamilyCode: (sessionId: string | null) =>
    ['funnelfox-family-code', sessionId] as const,

  wallet: () => ['wallet'] as const,
  pricingBabz: () => ['pricing', 'babz'] as const,
  streak: () => ['streak'] as const,
  streakDays: (start: string, end: string) =>
    ['streak', 'days', start, end] as const,
  starRoad: () => ['star-road'] as const,
  seasonsActive: () => ['seasons', 'active'] as const,
  seasonalLessonsAll: () => ['seasonal_lessons'] as const,
  seasonalLessons: (seasonType: string) =>
    ['seasonal_lessons', seasonType] as const,
  missions: () => ['missions'] as const,
  milestones: () => ['milestones'] as const,
  poll: () => ['poll'] as const,
  babzBoosters: () => ['babz_boosters'] as const,
  boostCurrent: () => ['boost', 'current'] as const,
  boostChildren: () => ['boost', 'children'] as const,
  boostValidities: () => ['boost', 'validity'] as const,
  boostValidity: (childId: number | undefined) =>
    ['boost', 'validity', childId] as const,

  badges: () => ['badges'] as const,
  badgesUnseenCount: () => ['badges', 'unseen-count'] as const,
  mates: () => ['mates'] as const,
  notifications: () => ['notifications'] as const,
  unreadNotifications: () => ['unread_notifications'] as const,
  unreadNotificationsNotifications: () =>
    ['unread_notifications', 'notifications'] as const,

  lessons: () => ['lessons'] as const,
  lessonsList: ({
    subjectMatters,
    size,
    mode,
  }: {
    subjectMatters?: string[];
    size?: number;
    mode?: 'all' | 'focus';
  }) =>
    [
      'lessons',
      `${subjectMatters?.join(',')}_${size}_${mode}`,
    ] as const,
  childLessons: ({
    childId,
    subjectMatters,
    size,
  }: {
    childId: number;
    subjectMatters?: string[];
    size?: number;
  }) => ['lessons', { childId, subjectMatters, size }] as const,
  lesson,
  lessonChapterFlow: (lessonId: number) =>
    ['lessons', lessonId, 'chapter_flow'] as const,
  lessonChapterSmartbits: (lessonId: number, chapterId: number) =>
    ['lessons', lessonId, 'chapters', chapterId, 'smartbits'] as const,
  lessonResources: (lessonId: number) =>
    ['lessons', lessonId, 'resources'] as const,
  lessonFlashcardFlowLatest: (lessonId: number) =>
    ['lessons', lessonId, 'flashcard-flow-latest'] as const,
  lessonFunFacts: (lessonId: number) =>
    ['lessons', lessonId, 'fun-facts'] as const,
  lessonDigest: (lessonId: number) => ['lessons', lessonId, 'digest'] as const,
  lessonOverallSummaries: (lessonId: number) =>
    ['lessons', lessonId, 'overall_summaries'] as const,
  lessonKnowledge: (lessonId: number) =>
    ['lessons', lessonId, 'knowledge'] as const,
  lessonPieces: (lessonPieceId: number | string | null) =>
    ['lesson_pieces', lessonPieceId] as const,
  lessonPhotos: (lessonId: number, refs: string) =>
    ['lesson-photos', lessonId, refs] as const,
  sharedLessonPreview: (lessonUuid: string | undefined) =>
    ['shared-lesson-preview', lessonUuid] as const,

  exams: () => ['exams'] as const,
  examsList: ({
    size,
    userId,
    createdBy,
  }: {
    size?: number;
    userId?: number;
    createdBy?: 'parent' | 'child';
  }) => ['exams', { size, userId, createdBy }] as const,
  exam,
  pieceParent: (
    queryRoot: 'exams' | 'lessons',
    parentId: number | undefined,
  ) => (queryRoot === 'exams' ? exam(parentId) : lesson(parentId)),
  timetables: () => ['timetables'] as const,

  parkourTimelines: () => ['parkour_timelines'] as const,
  parkourTimelinesForLesson: (lessonId: number) =>
    ['parkour_timelines', 'lesson', lessonId] as const,
  summerParkour: () => ['summer_parkour'] as const,
  summerParkourForSubject: (subject: string) =>
    ['summer_parkour', subject] as const,

  quizQuestions: (quizId: number) => ['quiz', quizId] as const,
  quizRuns: () => ['quizRun'] as const,
  quizRun: (quizId: number, quizRunId: number) =>
    ['quizRun', quizId, quizRunId] as const,
  challenge: (challengeId: number) => ['challenge', challengeId] as const,
  challenges: () => ['challenges'] as const,

  subjectsCatalog: (country: string | undefined) =>
    ['subjects', 'catalog', country ?? 'default'] as const,
  educationLevels: (country: string | null | undefined) =>
    ['education-levels', country ?? 'default'] as const,

  vsaqConversations: () => ['vsaq-chat', 'conversations'] as const,
  vsaqConversationsByChapters: (chapterUuids: string[]) =>
    ['vsaq-chat', 'conversations', 'by-chapters', ...chapterUuids] as const,
  vsaqConversation: (uuid: string) =>
    ['vsaq-chat', 'conversations', 'detail', uuid] as const,
  vsaqConversationWithHistory: (uuid: string) =>
    ['vsaq-chat', 'conversations', 'detail', uuid, 'history'] as const,
  tutorHistoryList: () => ['tutor-history', 'list'] as const,
  tutorHistoryConversation: (uuid: string) =>
    ['tutor-history', 'conversation', uuid] as const,
};
